/**
 * AppLauncherPopover — Popover de atalhos do sistema (estilo Gmail)
 *
 * Estrutura:
 *   - Campo de pesquisa (filtra a lista completa por label).
 *   - Cabeçalho "Seus favoritos" com botão de edição (jiggle + remover).
 *   - Grade de favoritos (vazia com CTA quando o usuário nunca configurou).
 *   - Divisor + lista completa "Todos os atalhos" com rolagem quando extrapola.
 *   - Ao clicar na estrela em "Todos", o item vira favorito instantaneamente.
 *
 * RELEASE: 2026-07-24 v1.5.0 - Campo de pesquisa no topo. Quando preenchido,
 *   oculta favoritos/categorias e exibe grade única de resultados (match no
 *   label, case-insensitive, sem acentos). Empty state e botão "×" para limpar.
 * RELEASE: 2026-07-24 v1.4.1 - Corrigida altura efetiva do ScrollArea.
 * RELEASE: 2026-07-24 v1.4.0 - Rolagem unificada.
 * RELEASE: 2026-07-24 v1.3.0 - Renderização por categorias temáticas.
 * RELEASE: 2026-07-24 v1.2.0 - Altura fixa (3 linhas) e scroll interno.
 * RELEASE: 2026-07-24 v1.1.0 - Layout flex robusto (max-h + flex-1 min-h-0).
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Check, LayoutGrid, Search, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAvailableShortcuts } from '@/hooks/useAvailableShortcuts';
import { useAppShortcutsPrefs } from '@/hooks/useAppShortcutsPrefs';
import { ShortcutTile } from './ShortcutTile';
import { SHORTCUTS_BY_KEY, SHORTCUT_CATEGORIES } from '@/config/appShortcuts';

interface AppLauncherPopoverProps {
  onClose: () => void;
}

/** Normaliza string para comparação (lowercase + remove acentos). */
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

export function AppLauncherPopover({ onClose }: AppLauncherPopoverProps) {
  const navigate = useNavigate();
  const available = useAvailableShortcuts();
  const { favorites, isFavorite, toggleFavorite } = useAppShortcutsPrefs();
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState('');

  // Set de chaves permitidas para o usuário atual (segurança + higiene UX).
  const availableKeys = useMemo(() => new Set(available.map((s) => s.key)), [available]);

  // Favoritos resolvidos + filtrados por disponibilidade real.
  const favoriteShortcuts = useMemo(() => {
    return favorites
      .filter((k) => availableKeys.has(k) && SHORTCUTS_BY_KEY[k])
      .map((k) => SHORTCUTS_BY_KEY[k]);
  }, [favorites, availableKeys]);

  const searchTerm = search.trim();
  const isSearching = searchTerm.length > 0;

  // Resultado da busca: lista plana filtrada por label normalizado.
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = normalize(searchTerm);
    return available.filter((s) => normalize(s.label).includes(q));
  }, [available, isSearching, searchTerm]);

  const handleNavigate = (route: string) => {
    navigate(route);
    onClose();
  };

  return (
    // v1.4.1: altura real é obrigatória para o Viewport interno do Radix rolar.
    <div className="flex h-[min(68dvh,var(--radix-popover-content-available-height,68dvh))] max-h-[68vh] w-[min(92vw,22rem)] flex-col overflow-hidden">
      {/* Campo de pesquisa (fixo no topo, fora do ScrollArea para não rolar junto) */}
      <div className="border-b border-border/60 px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar atalho..."
            autoFocus
            className="h-8 bg-muted/40 pl-7 pr-7 text-xs"
          />
          {isSearching && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Limpar busca"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <ScrollArea
        axis="y"
        touchAxis="pan-y"
        preventScrollChaining
        reserveScrollbarSpace
        className="h-full min-h-0 flex-1"
      >
        {isSearching ? (
          /* Modo busca: apenas grade única de resultados */
          <div className="px-2 pb-2 pt-2">
            <div className="px-1 pb-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Resultados ({searchResults.length})
              </p>
            </div>
            {searchResults.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/70 bg-muted/30 px-3 py-6 text-center text-xs text-muted-foreground">
                Nenhum atalho encontrado para "{searchTerm}".
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {searchResults.map((s) => (
                  <ShortcutTile
                    key={s.key}
                    shortcut={s}
                    onNavigate={handleNavigate}
                    onToggleFavorite={toggleFavorite}
                    isFavorite={isFavorite(s.key)}
                    editMode={false}
                    showFavoriteToggle
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Cabeçalho */}
            <div className="flex items-center justify-between px-3 pt-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <LayoutGrid className="h-3.5 w-3.5" />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">Seus favoritos</p>
                  <p className="text-[11px] text-muted-foreground">
                    Acesso rápido aos atalhos que você mais usa
                  </p>
                </div>
              </div>
              {favoriteShortcuts.length > 0 && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setEditMode((v) => !v)}
                  aria-label={editMode ? 'Concluir edição' : 'Editar favoritos'}
                >
                  {editMode ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                </Button>
              )}
            </div>

            {/* Favoritos */}
            <div className="px-2 pt-2">
              {favoriteShortcuts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/70 bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
                  Nenhum favorito ainda. Clique na estrela em qualquer atalho abaixo
                  para adicioná-lo aqui.
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-1">
                  {favoriteShortcuts.map((s) => (
                    <ShortcutTile
                      key={s.key}
                      shortcut={s}
                      onNavigate={handleNavigate}
                      onToggleFavorite={toggleFavorite}
                      isFavorite
                      editMode={editMode}
                      showFavoriteToggle={false}
                    />
                  ))}
                </div>
              )}
            </div>

            <Separator className="my-3" />

            {/* Título "Todos os atalhos" */}
            <div className="px-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Todos os atalhos
              </p>
            </div>

            {/* Lista por categorias */}
            <div className="px-2 pb-2 pt-1">
              {available.length === 0 ? (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  Nenhum atalho disponível para o seu perfil.
                </div>
              ) : (
                <div className="space-y-3 pr-2">
                  {SHORTCUT_CATEGORIES.map((cat) => {
                    const items = cat.shortcutKeys
                      .filter((k) => availableKeys.has(k) && SHORTCUTS_BY_KEY[k])
                      .map((k) => SHORTCUTS_BY_KEY[k]);
                    if (items.length === 0) return null;
                    return (
                      <div key={cat.key} className="space-y-1">
                        {cat.title && (
                          <p className="px-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {cat.title}
                          </p>
                        )}
                        <div className="grid grid-cols-4 gap-1">
                          {items.map((s) => (
                            <ShortcutTile
                              key={s.key}
                              shortcut={s}
                              onNavigate={handleNavigate}
                              onToggleFavorite={toggleFavorite}
                              isFavorite={isFavorite(s.key)}
                              editMode={false}
                              showFavoriteToggle
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </ScrollArea>
    </div>
  );
}
